/**
 * Utility functions for handling API responses consistently
 */

/**
 * Extract data from API response
 * @param {Object} response - API response object with status and data properties
 * @returns {*} The response data if status is 200, null otherwise
 */
export function extractData(response) {
    return response?.status === 200 ? response.data : null;
}

/**
 * Extract data from API response with error handling
 * @param {Object} response - API response object with status and data properties
 * @param {*} defaultValue - Default value to return if response is not successful
 * @returns {*} The response data if status is 200, defaultValue otherwise
 */
export function extractDataWithDefault(response, defaultValue = null) {
    return response?.status === 200 ? response.data : defaultValue;
}

/**
 * Check if API response is successful
 * @param {Object} response - API response object with status property
 * @returns {boolean} True if status is 200, false otherwise
 */
export function isSuccess(response) {
    return response?.status === 200;
}

/**
 * True when cached React Query data is an API envelope with a non-200 status.
 * api.get returns {status, data} without throwing, so RQ would otherwise treat
 * 502/etc. as a successful cache entry.
 */
export function isFailedApiEnvelope(data) {
    return !!(data && typeof data === 'object' && 'status' in data && data.status !== 200);
}

/**
 * Remove every query whose cached data is a failed API envelope.
 * Call on server pending→ready so loaders/useQuery do not keep boot-time 502s.
 */
export function purgeFailedApiQueries(queryClient) {
    for (const query of queryClient.getQueryCache().getAll()) {
        if (isFailedApiEnvelope(query.state.data)) {
            queryClient.removeQueries({ queryKey: query.queryKey });
        }
    }
}

/**
 * fetchQuery wrapper for route loaders: drop poisoned non-200 cache, and by
 * default throw when the API envelope is not status 200 so RQ does not store
 * failures as successful data.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {Object} options - fetchQuery options plus optional allowNon200
 * @param {boolean} [options.allowNon200=false] - if true, return non-200 envelopes
 */
export async function fetchApiQuery(queryClient, options) {
    const { queryFn, allowNon200 = false, ...rest } = options;
    const { queryKey } = rest;

    if (queryKey) {
        const cached = queryClient.getQueryData(queryKey);
        if (isFailedApiEnvelope(cached)) {
            queryClient.removeQueries({ queryKey });
        }
    }

    return queryClient.fetchQuery({
        ...rest,
        queryFn: async (...args) => {
            const resp = await queryFn(...args);
            if (!allowNon200 && resp?.status !== 200) {
                const detail =
                    typeof resp?.data === 'string'
                        ? resp.data
                        : (resp?.data?.message || '');
                throw new Error(
                    detail
                        ? `Request failed (HTTP ${resp?.status ?? 'unknown'}): ${detail}`
                        : `Request failed (HTTP ${resp?.status ?? 'unknown'})`
                );
            }
            return resp;
        },
    });
}
